import React, {
    forwardRef,
    PropsWithChildren,
    RefObject,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import './templatized-text-field.scss'
import classnames from 'classnames'
import { Input, InputRef, Popover } from 'antd'
import { Form } from '@ant-design/compatible'
import { useDebouncedValue } from '../../hooks/use-debounced-value'
import { SizeType } from 'antd/lib/config-provider/SizeContext'
import {
    MacroManager,
    MacroOptionType,
    MacroOptions as BaseMacroOptions,
    MacroType,
} from '@pushly/aqe/lib/components/macro-manager/macro-manager'
import EmojiManager from '@pushly/aqe/lib/components/emoji-manager/emoji-manager'
import { CompressOutlined, ExpandOutlined } from '@ant-design/icons'

export type InputFocusRef = {
    /**
     * Programmatically focus the underlying input or textarea.
     * Allows a parent component to move focus into this field on demand.
     */
    focusInput(): void
} & InputRef

/**
 * Character limit and messaging controls.
 * - min/max: numeric constraints
 * - above/below messages: human-friendly guidance
 * - messageOverride: show this instead of the auto "N Characters" text
 * - maxConstraint: when true, the input's maxLength is set (hard limit). When false, we only warn (soft limit).
 */
export type Limits = {
    min?: number
    max?: number
    aboveMaxMessage?: string
    belowMinMessage?: string
    messageOverride?: string
    maxConstraint?: boolean
}

/**
 * Render modes:
 * - 'default' -> single-line Input
 * - 'textarea' -> multi-line Input.TextArea
 */
type TemplatizedTextFieldMode = 'default' | 'textarea'

/**
 * Props for TemplatizedTextField.
 * Configure appearance, behavior, and optional helpers (macros, emojis, limits).
 */
export type ITemplatizedTextField = {
    /** Optional class for the outer container */
    className?: string
    /** Optional class for the input element */
    inputClassName?: string
    defaultMode?: TemplatizedTextFieldMode
    mode?: TemplatizedTextFieldMode
    showModeToggle?: boolean
    onModeChange?: (mode: TemplatizedTextFieldMode) => void
    modeToggleTooltip?: React.ReactNode
    disabled?: boolean
    defaultValue?: string | null
    value?: string | null
    onChange?: (value?: string) => void
    label?: string | React.ReactNode
    size?: SizeType
    hideMacros?: boolean
    macroTypes?: string[]
    customMacroOptions?: any
    secretsMacroOptions?: any
    validMacroOptionTypes?: MacroOptionType[]
    hideEmojis?: boolean
    placeholder?: string
    limits?: Limits
    /** HTML autocomplete attribute pass-through */
    autoComplete?: string
}

type CharacterLimitWrapperProps = {
    /** The text currently in the input (after trimming) */
    value: string
    /** Rules for how to compute and show character limits */
    config: Limits
}

/**
 * Narrowed view of AntD Input/TextArea refs we care about.
 * AntD exposes different shapes for Input vs TextArea; this type captures both.
 */
type AntInputLike = {
    input?: HTMLInputElement
    resizableTextArea?: { textArea: HTMLTextAreaElement }
}

/**
 * Safely extract the native input/textarea element from an AntD Input or TextArea ref.
 * Returns null if the element cannot be resolved.
 *
 * @param ref - AntD input-like ref object (may be undefined/null during mounts)
 */
function extractInputElement(ref: AntInputLike | undefined | null) {
    if (ref?.input) {
        return ref.input
    } else if (ref?.resizableTextArea) {
        return ref.resizableTextArea.textArea
    } else {
        return null
    }
}

const FragmentWrapper = (_: any) => _.children

/**
 * Displays a small message with the current character count and limit warnings.
 * Shows "how long the text is" and warns when it violates min/max guidance.
 */
const CharacterLimitWrapper: React.FunctionComponent<PropsWithChildren<CharacterLimitWrapperProps>> = (_) => {
    const { children, value, config, ...props } = _

    const currVal = (value || '').toString().trim()
    const currLen = currVal.length
    const aboveMax = !config.max ? false : config.max < currLen
    const belowMin = !config.min ? false : config.min > currLen
    const constraintMet = !!config.max && (!config.maxConstraint ? false : config.max <= currLen)

    let msg = `${currLen} ${currLen === 1 ? 'Character' : 'Characters'}`
    if (belowMin && !!config.belowMinMessage) {
        msg += `. ${config.belowMinMessage}`
    } else if (aboveMax && !!config.aboveMaxMessage) {
        msg += `. ${config.aboveMaxMessage}`
    }

    return (
        <span
            className={classnames('char-limit-wrapper', {
                'outside-limits': belowMin || aboveMax,
                'constraint-met': constraintMet,
            })}
        >
            {children}
            <span className={classnames('char-limit-message')}>{config.messageOverride ?? msg}</span>
        </span>
    )
}

const TemplatizedTextField = forwardRef<InputFocusRef, ITemplatizedTextField>((props, ref) => {
    const {
        defaultMode = 'default',
        mode: explicitMode,
        showModeToggle = false,
        onModeChange,
        modeToggleTooltip,
        disabled,
        onChange,
        label,
        size,
        hideEmojis,
        hideMacros,
        customMacroOptions,
        secretsMacroOptions,
        validMacroOptionTypes,
        placeholder,
        className,
        inputClassName,
    } = props

    /**
     * Debounced value:
     * Defers notifying parents about changes by a short delay to reduce churn on every keystroke.
     * Improves typing performance and avoids unnecessary renders/network calls.
     */
    const [value, setValue] = useDebouncedValue<string>(
        (v) => {
            onChange?.(v)
        },
        (props.value ?? props.defaultValue ?? undefined) as string | undefined,
        320,
    )

    /**
     * Internal mode state:
     * Uses a controlled mode if provided; otherwise, falls back to internal state.
     */
    const [mode, setMode] = React.useState<TemplatizedTextFieldMode>(explicitMode ?? defaultMode)

    const EmojiWrapper = !hideEmojis ? EmojiManager : FragmentWrapper
    const MacroWrapper = !hideMacros ? MacroManager : FragmentWrapper
    const LimitWrapper = !!props.limits ? CharacterLimitWrapper : FragmentWrapper

    const InputComponent = useMemo(() => (mode === 'default' ? Input : Input.TextArea), [mode])
    const ModeToggleComponent = useMemo(() => (mode === 'default' ? ExpandOutlined : CompressOutlined), [mode])

    const currVal = (value ?? props.value ?? props.defaultValue ?? '').toString().trim()
    const macroDetected = /({{)|({%)/.test(currVal)
    const hasSecretsMacroOptions = Array.isArray(secretsMacroOptions) && secretsMacroOptions.length > 0
    const baseMacroTypes = (props.macroTypes as any) ?? ['location', 'secrets', 'custom']
    const visibleMacroTypes = hasSecretsMacroOptions
        ? baseMacroTypes
        : baseMacroTypes.filter((type: string) => type !== 'secrets')

    // When macros are present and custom options are enabled, length can be dynamic and not precisely measurable.
    const limits: Limits = {
        ...(props.limits ?? {}),
    }
    if (!hideMacros && (!!customMacroOptions || hasSecretsMacroOptions) && macroDetected) {
        limits.messageOverride = 'Unable to calculate characters due to dynamic macro length'
    }

    const macroOptions = useMemo(() => {
        if (!hasSecretsMacroOptions) {
            return undefined
        }

        return {
            ...BaseMacroOptions,
            secrets: {
                label: 'Secrets',
                options: secretsMacroOptions,
            },
        }
    }, [hasSecretsMacroOptions, secretsMacroOptions])

    let actionItemWidth = showModeToggle ? 1 : 0
    if (!hideEmojis) actionItemWidth += 1
    if (!hideMacros) actionItemWidth += 1

    /**
     * If a value (from props or paste) contains newlines and the toggle is available,
     * automatically switch to multi-line mode for a better editing experience.
     *
     * @param v - the candidate value to inspect for newline characters
     */
    const checkExplicitValueChangeMode = useCallback(
        (v: string | undefined | null) => {
            if (/\n/.test(v ?? '') && showModeToggle) {
                setMode('textarea')
            }
        },
        [showModeToggle],
    )

    /**
     * Keep internal state aligned with external controlled value.
     * If the parent changes the value, mirror it here.
     */
    React.useEffect(() => {
        const propsValue = props.value ?? props.defaultValue
        if (propsValue?.trim() !== value?.trim()) {
            setValue(propsValue ?? '', false)
            checkExplicitValueChangeMode(propsValue)
        }
    }, [props.value, props.defaultValue, showModeToggle])

    /**
     * Handle paste events to normalize pasted content and optionally shift modes.
     * Prevents the browser default paste to ensure we control the inserted value.
     *
     * Note: event typing is intentionally generic here to avoid coupling to a specific input type.
     * @param ev - Clipboard event from the input/textarea
     */
    const handlePasteEvent = (ev: any) => {
        // Paste intercept is only available for single-line inputs in togglable mode
        if (!showModeToggle || mode !== 'default') return
        ev.preventDefault()

        try {
            const pastedText = ev.clipboardData?.getData('text/plain') ?? ''
            const el = (ev.target ?? ev.currentTarget) as HTMLInputElement | HTMLTextAreaElement
            if (!el) return

            const currentValue = el.value ?? ''
            const selStart = el.selectionStart ?? currentValue.length
            const selEnd = el.selectionEnd ?? selStart
            const nextValue = currentValue.slice(0, selStart) + pastedText + currentValue.slice(selEnd)

            setValue(nextValue)
            // Check mode based on the resulting value (contains any newlines)
            checkExplicitValueChangeMode(nextValue)

            // restore caret right after the inserted text
            const nextCaretPos = selStart + pastedText.length
            requestAnimationFrame(() => {
                try {
                    el.selectionStart = nextCaretPos
                    el.selectionEnd = nextCaretPos
                    el.focus()
                } catch {}
            })
        } catch {}
    }

    /**
     * Manage paste event listeners (instance-scoped).
     * We deregister and re-register on every render to ensure isolation and freshness.
     */
    const pasteHandlerRef = useRef<(e: ClipboardEvent) => void>()

    useEffect(() => {
        // Always rewire on every render
        const el = extractInputElement(inputRef.current) as HTMLInputElement | HTMLTextAreaElement | null
        if (!el) return

        // Clean up any previous wiring
        if (pasteHandlerRef.current) {
            el.removeEventListener('paste', pasteHandlerRef.current)
            pasteHandlerRef.current = undefined
        }

        // Create a fresh, element-specific handler
        const handler = (e: ClipboardEvent) => {
            // only handle paste for the exact element we wired
            if (e.target !== el) return
            e.stopPropagation?.()
            handlePasteEvent(e)
        }

        el.addEventListener('paste', handler, { passive: false })
        pasteHandlerRef.current = handler

        // Cleanup before the next render and on unmount
        return () => {
            if (el && pasteHandlerRef.current) {
                el.removeEventListener('paste', pasteHandlerRef.current)
                pasteHandlerRef.current = undefined
            }
        }
    })

    /**
     * Expose an imperative focus() method to parents via ref.
     * Enables parent components to focus this field directly.
     */
    const inputRef = useRef<any>(null)

    // Small timeout ensures the element exists and is focusable in the next tick.
    const focusInput = () => {
        setTimeout(() => {
            if (!inputRef.current) {
                return
            } else {
                ;(inputRef.current as any).focus()
            }
        }, 20)
    }

    useImperativeHandle(
        ref as RefObject<any>,
        () => ({
            focusInput: () => focusInput(),
        }),
        [],
    )

    return (
        <Form.Item className={className} label={label}>
            <span
                className={classnames('templatized-text-field', `mode-${mode}`, `has-action-count-${actionItemWidth}`, {
                    'mode-togglable': showModeToggle,
                    disabled: disabled ?? false,
                })}
            >
                <span className="templatized-text-field-mode-toggle">
                    {showModeToggle && (
                        <Popover
                            overlayClassName="templatized-text-field-mode-toggle-overlay"
                            content={
                                <>
                                    <p>
                                        {mode === 'default' ? (
                                            <span>
                                                <b>Expand</b> to multi-line view
                                            </span>
                                        ) : (
                                            <span>
                                                <b>Collapse</b> to single-line view
                                            </span>
                                        )}
                                    </p>
                                    {modeToggleTooltip}
                                </>
                            }
                        >
                            <ModeToggleComponent
                                onClick={() => {
                                    const nextMode = mode === 'default' ? 'textarea' : 'default'

                                    if (nextMode === 'default' && currVal) {
                                        const nextValue = currVal.replace(/\n+/g, ' ')
                                        setValue(nextValue)
                                        onChange?.(nextValue)
                                    }

                                    setMode(nextMode)
                                    onModeChange?.(nextMode)
                                }}
                            />
                        </Popover>
                    )}
                </span>
                <LimitWrapper value={currVal} config={limits}>
                    <EmojiWrapper
                        overlayClassName={classnames('ttf-wrapped', {
                            'ttf-mode-toggle-shifted': showModeToggle,
                        })}
                        disabled={disabled}
                    >
                        <MacroWrapper
                            overlayClassName={classnames('ttf-wrapped', {
                                'ttf-mode-toggle-shifted': showModeToggle,
                            })}
                            types={visibleMacroTypes}
                            options={macroOptions as any}
                            customOptions={customMacroOptions}
                            disabled={disabled}
                            validOptionTypes={validMacroOptionTypes}
                        >
                            <InputComponent
                                ref={(el) => (inputRef.current = el)}
                                maxLength={limits.maxConstraint ? limits?.max : undefined}
                                className={inputClassName}
                                disabled={disabled}
                                size={size}
                                placeholder={placeholder}
                                defaultValue={props.defaultValue ?? undefined}
                                value={value ?? props.value}
                                onChange={(ev) => {
                                    const nextValue = ev.target.value
                                    setValue(nextValue)
                                }}
                                autoComplete={props.autoComplete}
                            />
                        </MacroWrapper>
                    </EmojiWrapper>
                </LimitWrapper>
            </span>
        </Form.Item>
    )
})

export default TemplatizedTextField
